import classNames from "classnames"
import type { FC, FocusEvent, ChangeEvent, KeyboardEvent } from "react"
import { lazy, useState, useEffect, useRef, useId, Suspense } from "react"
import { useNavigate } from "react-router-dom"
import useOutsideClick from "~/hooks/useOutsideClick"
import useSearch from "~/hooks/useSearch"
import Cross from "~/icons/Cross"

interface Props {
  initialQuery: string
  onQueryChange: (query: string) => void
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  inputClass?: string
  autoFocus?: boolean
  showClearButton?: boolean
  isShowingPopover?: boolean
  onSearch?: (query: string) => void
  onFocus?: () => void
}

const SanitizedText = lazy(() => import("~/components/SanitizedText"))

const SearchInput: FC<Props> = ({
  initialQuery,
  onQueryChange,
  leftIcon,
  rightIcon,
  inputClass,
  autoFocus = false,
  showClearButton = false,
  isShowingPopover = false,
  onSearch,
  onFocus
}) => {
  const {
    fetchSuggestions,
    suggestions,
    activeSuggestionIndex,
    clearSuggestions,
    clearActiveSuggestionIndex,
    nextSuggestion,
    previousSugggestion
  } = useSearch()
  const navigate = useNavigate()
  const id = useId()

  const [value, setValue] = useState(initialQuery)

  const selectSuggestion = (suggestion: (typeof suggestions)[0]) => {
    if (suggestion.href) {
      navigate(suggestion.href)
    } else {
      setValue(suggestion.text)
      onQueryChange(suggestion.text)
      clearSuggestions()
    }
  }

  useEffect(() => {
    if (isShowingPopover && suggestions.length > 0) clearSuggestions()
  }, [clearSuggestions, suggestions.length, isShowingPopover])

  const searchInputValue = () => {
    onQueryChange(value)
    clearSuggestions()
    if (onSearch) onSearch(value)
  }

  const clearInputValue = () => {
    setValue("")
    clearSuggestions()

    // We need to focus after re-render because otherwise the input would fetch suggestions for the stale value.
    setTimeout(() => inputRef.current?.focus(), 25)
  }

  const containerRef = useOutsideClick({
    onOutsideClick: () => {
      if (value != initialQuery) {
        onQueryChange(value)
      }

      clearSuggestions()
    }
  })

  const inputRef = useRef<HTMLInputElement>(null)

  const showingSuggestions = suggestions!.length > 0

  return (
    <div className="relative mx-auto max-w-md">
      <div ref={containerRef} className="w-full">
        <div className="relative w-full">
          {leftIcon}
          <input
            ref={inputRef}
            autoFocus={autoFocus}
            type="text"
            className={classNames(
              inputClass,
              "relative block w-full pr-16 text-base outline-0 ring-4 focus:ring-green-dark-50 placeholder-gray-300",
              {
                "ring-transparent": !showingSuggestions,
                "rounded-b-none ring-green-dark-50": showingSuggestions
              }
            )}
            placeholder={initialQuery.length == 0 ? "Search" : "disney"}
            value={value}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setValue(e.target.value)
              fetchSuggestions(e.target.value)
            }}
            onFocus={(e: FocusEvent<HTMLInputElement>) => {
              fetchSuggestions(e.target.value)
              onFocus?.()
            }}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key == "ArrowDown") {
                nextSuggestion()

                e.preventDefault()
                e.stopPropagation()
              } else if (e.key == "ArrowUp") {
                previousSugggestion()

                e.preventDefault()
                e.stopPropagation()
              } else if (e.key == "Enter") {
                if (
                  activeSuggestionIndex !== null &&
                  suggestions[activeSuggestionIndex]
                ) {
                  selectSuggestion(suggestions[activeSuggestionIndex])
                } else {
                  searchInputValue()
                }

                e.preventDefault()
                e.stopPropagation()
              } else if (e.key == "Escape") {
                clearSuggestions()

                e.preventDefault()
                e.stopPropagation()
              }
            }}
            id={id}
            aria-label="Search term"
            aria-autocomplete="list"
            role="combobox"
            aria-expanded={showingSuggestions}
            aria-controls={`${id}-suggestions`}
            aria-describedby={`${id}-suggestions`}
            aria-haspopup="listbox"
          />
          {showClearButton && value && (
            <button
              title="Clear"
              type="button"
              onClick={clearInputValue}
              className="absolute right-4 top-1/2 z-10 hidden -translate-y-1/2 rounded-full pl-3 text-camp-primary  transition-opacity duration-200 hover:opacity-70 lg:block"
            >
              <Cross />
            </button>
          )}
          {rightIcon}
        </div>

        <div className="relative w-full">
          <div
            onMouseEnter={clearActiveSuggestionIndex}
            className={classNames(
              "absolute inset-x-0 top-1 z-20 flex-col overflow-hidden rounded-b-2xl bg-white shadow-lg ring-4 ring-green-dark-50",
              {
                hidden: !showingSuggestions
              }
            )}
          >
            <div
              id={`${id}-suggestions`}
              role="listbox"
              aria-label="Search suggestions"
              className="divide-y divide-gray-50"
            >
              {suggestions.map((suggestion, index) => (
                <button
                  key={suggestion.id}
                  role="menuitem"
                  onClick={e => {
                    e.currentTarget.blur()
                    selectSuggestion(suggestion)
                  }}
                  className={classNames(
                    "flex w-full items-center justify-between space-x-4 py-3 pl-6 pr-3 text-left text-base leading-normal text-camp-primary hover:bg-cream-50",
                    {
                      "bg-cream-50": index === activeSuggestionIndex
                    }
                  )}
                >
                  {/* Algolia returns HTML entities instead of &'s and other characters that we originally put into indexes - we need to render this as HTML. */}
                  <Suspense fallback={null}>
                    <SanitizedText text={suggestion.text} />
                  </Suspense>
                  {suggestion.badge && (
                    <div className="whitespace-nowrap rounded-full bg-cream-500 px-3 py-1 text-base text-camp-primary">
                      {suggestion.badge}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SearchInput
